import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CoachCommentItem } from '../types';
import { useTranslation } from '../../../i18n';

interface CoachCommentsCardProps {
  comments?: CoachCommentItem[];
  feedbackSummary?: string;
  evaluatedBy?: string;
  evaluatorRole?: string;
  learnerName?: string;
  defaultCoachName?: string;
  defaultCoachRole?: string;
  /** Changes when the parent switches attempts, so learner replies reset. */
  resetKey?: string;
}

export const CoachCommentsCard: React.FC<CoachCommentsCardProps> = ({
  comments,
  feedbackSummary,
  evaluatedBy,
  evaluatorRole = 'AI Evaluation Coach',
  learnerName = 'Learner',
  defaultCoachName = 'AI Coach',
  defaultCoachRole = 'Evaluator',
  resetKey,
}) => {
  const { t } = useTranslation();
  const coachDisplayName = evaluatedBy || defaultCoachName;
  const coachDisplayRole = evaluatorRole || defaultCoachRole;
  const coachInitials = coachDisplayName
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'AC';

  const learnerInitials = learnerName
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'L';

  const [replyText, setReplyText] = useState('');
  const [localReplies, setLocalReplies] = useState<CoachCommentItem[]>([]);

  // Coach comments are derived from props, never copied into state: a copied
  // array plus a sync effect re-triggers itself on every render (the default
  // `comments = []` produced a new array identity each time), which caused
  // "Maximum update depth exceeded".
  const baseComments = useMemo<CoachCommentItem[]>(() => {
    if (comments && comments.length > 0) {
      return comments;
    }
    if (feedbackSummary && feedbackSummary.trim()) {
      return [
        {
          id: 'c-eval-1',
          authorName: coachDisplayName,
          authorRole: coachDisplayRole,
          authorInitials: coachInitials,
          createdAt: 'Just now',
          text: feedbackSummary.trim(),
        },
      ];
    }
    return [];
  }, [comments, feedbackSummary, coachDisplayName, coachDisplayRole, coachInitials]);

  // Only learner replies are local state. Clear them when the parent switches
  // attempts so notes from the previous attempt do not carry over.
  const previousResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) return;
    previousResetKeyRef.current = resetKey;
    setLocalReplies([]);
  }, [resetKey]);

  const displayedComments = useMemo(
    () => (localReplies.length > 0 ? [...baseComments, ...localReplies] : baseComments),
    [baseComments, localReplies],
  );

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    const text = replyText.trim();
    if (!text) return;

    setLocalReplies((prev) => [
      ...prev,
      {
        id: `c-${Date.now()}`,
        authorName: learnerName,
        authorRole: 'Learner',
        authorInitials: learnerInitials,
        createdAt: 'Just now',
        text,
      },
    ]);
    setReplyText('');
  };

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header with SVG Icon */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#E8F0FE',
              color: '#0D4FBD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h16v11H9l-5 4z" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.coach_comments_title', undefined, 'Coach comments')}
          </h2>
        </div>

        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {displayedComments.length === 1
            ? t('scorecard.comments_count_single', undefined, '1 comment')
            : t('scorecard.comments_count_plural', { count: displayedComments.length }, `${displayedComments.length} comments`)}
        </span>
      </div>

      {/* Comment Thread */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {displayedComments.map((comment, idx) => (
          <div
            key={comment.id || idx}
            style={{
              background: '#F8FAFC',
              border: '1px solid #F1F5F9',
              borderRadius: '10px',
              padding: '14px 16px',
              display: 'flex',
              gap: '12px',
            }}
          >
            {/* Avatar Circle */}
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: '#0E7C86',
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {comment.authorInitials || 'AR'}
            </div>

            {/* Comment Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>
                  {comment.authorName}
                </span>

                {comment.authorRole && (
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      background: '#F3E8FF',
                      color: '#6D28D9',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}
                  >
                    {comment.authorRole}
                  </span>
                )}

                <span style={{ fontSize: '11px', color: 'var(--faint)' }}>
                  · {comment.createdAt || 'Just now'}
                </span>
              </div>

              <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: '#334155', lineHeight: 1.45, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {comment.text}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Inline Reply Box matching Screenshot */}
      <form
        onSubmit={handleSendReply}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#FFFFFF',
          border: '1px solid #CBD5E1',
          borderRadius: '10px',
          padding: '6px 8px 6px 14px',
        }}
      >
        <input
          type="text"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={t('scorecard.reply_placeholder', undefined, `Reply to ${coachDisplayName.split(' ')[0]}...`)}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            fontSize: '16px',
            color: 'var(--navy)',
            background: 'transparent',
          }}
        />
        <button
          type="submit"
          disabled={!replyText.trim()}
          style={{
            padding: '7px 18px',
            borderRadius: '8px',
            background: '#0D4FBD',
            color: '#FFFFFF',
            border: 'none',
            fontSize: '12.5px',
            fontWeight: 700,
            cursor: replyText.trim() ? 'pointer' : 'default',
            opacity: replyText.trim() ? 1 : 0.7,
            transition: 'all 0.15s ease',
          }}
        >
          {t('scorecard.reply_btn', undefined, 'Reply')}
        </button>
      </form>
    </div>
  );
};
